import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, Input, Space, Tag, Tooltip } from 'antd';
import { IconBuilding, IconFileText } from '@tabler/icons-react';
import { CrudMode, CrudSchemaFields, type EnsoFormItem } from '@ohpen/enso-ui';
import { ExportOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { CasePermission, FieldType, FirmType } from '@pmp/api';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, textLimitProps } from '@pmp/utils';
import { PmpConfigContext } from '@pmp/contexts';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { getCompaniesHouseUrl, getHighlightProps, resetFieldsOnTypeChange } from '../utils';
import styles from '../styles.module.css';

export const RegistrationDetails: React.FC<TabProps> = ({
  form,
  mode,
  selectsOptions,
  entityId,
  populateFcaData,
  fcaModifiedFields,
  removeFcaModifiedField,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const readonly = mode === CrudMode.VIEW;
  const frn = Form.useWatch(['registrationDetails', 'frn'], form);
  const type = Form.useWatch(['registrationDetails', 'type'], form);
  const operations: string[] | undefined = Form.useWatch(['registrationDetails', 'operations'], form);
  const companiesHouseNumber = Form.useWatch(['registrationDetails', 'companiesHouseNumber'], form);
  const { isFlexibleProcFeeActive } = useContext(PmpConfigContext);

  const showPackagingWithAdviceWarning = isFlexibleProcFeeActive && operations?.includes('ADVICE') && operations?.includes('PACKAGE');

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);
  // Mirrors the server rule "FRN is required for FCA-registered firms" — unlike isFcaRegistered,
  // this stays false while no type is selected yet.
  const frnRequired = useMemo(
    () => type === FirmType.APPOINTED_REPRESENTATIVE || type === FirmType.DIRECTLY_AUTHORISED,
    [type],
  );

  const populateData = useCallback(() => {
    setLoading(true);
    if (populateFcaData) {
      populateFcaData(frn).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [frn, populateFcaData]);

  const firmColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('firms.labels.currentStatus'),
        dataIndex: ['registrationDetails', 'isActive'],
        valueType: 'switch',
        initialValue: mode === CrudMode.CREATE ? false : undefined,
        readonly,
        fieldProps: readonly
          ? undefined
          : {
              checkedChildren: t('firms.labels.active'),
              unCheckedChildren: t('firms.labels.inactive'),
            },
        readonlyDisplay: 'tag',
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('firms.labels.type'),
            dataIndex: ['registrationDetails', 'type'],
            valueType: 'select',
            fieldProps: {
              options: selectsOptions[FieldType.FIRM_TYPE],
              tagColor: 'blue',
              className: getHighlightProps(['registrationDetails', 'type'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'type'),
              onChange: (value: string) => resetFieldsOnTypeChange(form, value),
            },
            formItemProps: {
              label: (
                <span>
                  {t('firms.labels.type')}
                  {isFcaRegistered && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              required: true,
            },
            readonly,
            readonlyDisplay: 'tag',
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.principalFirm'),
            dataIndex: ['registrationDetails', 'principalIds'],
            valueType: 'select',
            fieldProps: {
              options: (selectsOptions[FieldType.PRINCIPALS] ?? []).filter(o => o.value !== entityId),
              tagColor: 'blue',
              mode: 'multiple',
              optionFilterProp: 'label',
            },
            formItemProps: {
              required: true,
              hidden: type !== FirmType.APPOINTED_REPRESENTATIVE,
            },
            readonly,
            readonlyDisplay: 'tag',
            colProps: { span: 12 },
          },
        ],
      },
      // {
      //   title: t('firms.labels.currentStatus'),
      //   dataIndex: ['registrationDetails', 'currentStatus'],
      //   valueType: 'checkbox',
      //   fieldProps: {
      //     options: [{ label: t('firms.labels.firmActive'), value: 'firmActive' }],
      //   },
      //   colProps: { span: 24 },
      // },
      // A group is rendered before its formItemProps are read, so `hidden` never reaches it — the
      // FRN block is kept out of the schema entirely for a firm that carries no FCA registration.
      ...(isFcaRegistered ? [{
        valueType: 'group' as const,
        colProps: { span: 24 },
        columns: [
          {
            title: t('firms.labels.frn'),
            dataIndex: ['registrationDetails', 'frn'],
            valueType: 'text' as const,
            readonly,
            renderText: (val: unknown) => String(val),
            renderFormItem: () => (
              <Space.Compact block>
                <Form.Item name={['registrationDetails', 'frn']} noStyle>
                  <Input inputMode="numeric" disabled={mode !== CrudMode.CREATE} />
                </Form.Item>
                <Button type="primary" onClick={populateData} disabled={readonly} loading={loading}>
                  {t('firms.buttons.populateData')}
                </Button>
              </Space.Compact>
            ),
            formItemProps: {
              tooltip: readonly ? null : t('firms.labels.frnPopulateTooltip'),
              required: frnRequired,
            },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.fcaRegister'),
            dataIndex: ['registrationDetails', 'fcaRegisterLink'],
            key: 'fcaRegisterLink',
            valueType: 'text' as const,
            colProps: { span: 12 },

            renderFormItem: () => {
              const frn = form?.getFieldValue(['registrationDetails', 'frn']);
              if (!frn) return '-';

              const url = `https://register.fca.org.uk/s/search?q=${encodeURIComponent(frn)}&type=Companies`;

              return (
                <a href={url} target="_blank" rel="noreferrer">
                  {t('firms.labels.goToWebsite')}
                  <ExportOutlined />
                </a>
              );
            },
          },
        ],
      }] : []),
      {
        valueType: 'group',
        columns: [
          {
            dataIndex: ['registrationDetails', 'companiesHouseNumber'],
            valueType: 'text',
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(['registrationDetails', 'companiesHouseNumber'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'companiesHouseNumber'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('firms.labels.companiesHouseNumber')}
                  {isFcaRegistered && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.companiesHouse'),
            dataIndex: ['registrationDetails', 'companiesHouseLink'],
            key: 'companiesHouseLink',
            valueType: 'text',
            colProps: { span: 12 },
            renderFormItem: () => {
              const url = getCompaniesHouseUrl(companiesHouseNumber);
              if (!url) return '-';

              return (
                <a href={url} target="_blank" rel="noreferrer">
                  {t('firms.labels.goToWebsite')}
                  <ExportOutlined />
                </a>
              );
            },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('firms.labels.name'),
            dataIndex: ['registrationDetails', 'name'],
            valueType: 'text',
            readonly,
            formItemProps: {
              label: (
                <span>
                  {t('firms.labels.name')}
                  {isFcaRegistered && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              required: true,
            },
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(['registrationDetails', 'name'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'name'),
            },
            colProps: { span: 12 },
          },
        ],
      },
      ...(mode === CrudMode.CREATE ? [{
        valueType: 'group' as const,
        columns: [
          {
            title: t('firms.labels.reference'),
            dataIndex: ['registrationDetails', 'reference'],
            valueType: 'text' as const,
            readonly,
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
            colProps: { span: 12 },
            formItemProps: {
              tooltip: readonly ? null : t('firms.labels.referenceTooltip'),
            },
          },
        ],
      }] : [{
        valueType: 'group' as const,
        columns: [
          {
            title: t('firms.labels.ohpenId'),
            dataIndex: ['registrationDetails', 'ohpenId'],
            valueType: 'text' as const,
            readonly: mode === CrudMode.VIEW,
            fieldProps: {
              disabled: true,
            },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.reference'),
            dataIndex: ['registrationDetails', 'reference'],
            valueType: 'text' as const,
            readonly: mode === CrudMode.VIEW,
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
            colProps: { span: 12 },
            formItemProps: {
              tooltip: mode === CrudMode.VIEW ? null : t('firms.labels.referenceTooltip'),
            },
          },
        ],
      }]),
    ],
    [t, populateData, readonly, mode, type, form, loading, selectsOptions, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered, frnRequired, entityId, companiesHouseNumber],
  );

  const registrationColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            dataIndex: ['registrationDetails', 'fcaStatus'],
            valueType: 'text',
            readonly: true,
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'fcaStatus'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'fcaStatus'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('firms.labels.fcaRegisterStatus')}
                  {isFcaRegistered && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.nacfbRegistration'),
            dataIndex: ['registrationDetails', 'nacfbNumber'],
            valueType: 'text',
            readonly,
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('firms.labels.operations'),
            dataIndex: ['registrationDetails', 'operations'],
            valueType: 'checkbox',
            fieldProps: {
              options: [
                { label: t('firms.labels.givesAdvice'), value: 'ADVICE' },
                { label: t('firms.labels.packaging'), value: 'PACKAGE' },
                { label: t('firms.labels.actAsPrincipal'), value: 'NETWORK', disabled: type !== FirmType.DIRECTLY_AUTHORISED },
                { label: t('firms.labels.mortgageClub'), value: 'MORTGAGE_CLUB', disabled: type === FirmType.NOT_FCA_REGISTERED },
              ],
              disabled: readonly,
              style: {
                display: 'flex',
                flexDirection: 'column',
                rowGap: 4,
              },
            },
            formItemProps: {
              required: true,
              extra: showPackagingWithAdviceWarning && (
                <Alert className={styles.operationsWarning} type="warning" showIcon title={t('firms.labels.packagingWithAdviceFlexibleProcFeeWarning')} />
              ),
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        colProps: { span: 24 },
        title: (
          <Space size={4}>
            {t('firms.labels.activitiesPermissions')}
            <Tooltip title={t('firms.labels.activitiesPermissionsTooltip')}>
              <QuestionCircleOutlined className={styles.tooltipIcon} />
            </Tooltip>
          </Space>
        ),
        columns: [
          {
            title: t('firms.labels.btl'),
            dataIndex: ['registrationDetails', 'btl'],
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.CASE_PERMISSION_TYPE] },
            formItemProps: { tooltip: t('firms.labels.btlTooltip') },
            initialValue: CasePermission.CREATE_AND_MANAGE,
            readonly,
            readonlyDisplay: 'tag',
            colProps: { span: 8 },
          },
          {
            title: t('firms.labels.rbtl'),
            dataIndex: ['registrationDetails', 'rbtl'],
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.CASE_PERMISSION_TYPE], disabled: !isFcaRegistered },
            formItemProps: { tooltip: t('firms.labels.rbtlTooltip') },
            initialValue: CasePermission.CREATE_AND_MANAGE,
            readonly,
            readonlyDisplay: 'tag',
            colProps: { span: 8 },
          },
          {
            title: t('firms.labels.rmc'),
            dataIndex: ['registrationDetails', 'rmc'],
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.CASE_PERMISSION_TYPE], disabled: !isFcaRegistered },
            initialValue: CasePermission.CREATE_AND_MANAGE,
            readonly,
            readonlyDisplay: 'tag',
            colProps: { span: 8 },
          },
        ],
      },
    ],
    [readonly, selectsOptions, t, type, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered, showPackagingWithAdviceWarning],
  );

  return (
    <>
      <CrudFormCard icon={<IconBuilding />} title={t('firms.labels.firmTitle')}>
        <CrudSchemaFields schema={firmColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconFileText />} title={t('firms.labels.registrationDetails')}>
        <CrudSchemaFields schema={registrationColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
    </>
  );
};
